import React, { useEffect, useState, useMemo } from "react";
import { Line } from "react-chartjs-2";
import { useMutation } from "@apollo/client";
import moment from "moment";
import {
  GRAPH_MUTATION,
  TRACK_GRAPH_MUTATION,
  USER_GRAPH_MUTATION,
  PROJECT_GRAPH_MUTATION,
  SUBS_GRAPH_MUTATION,
  STORAGE_GRAPH_MUTATION,
  CAMAROO_USER_GRAPH_MUTATION,
  PORTFOLIO_GRAPH_MUTATION,
  OPPORTUNITY_GRAPH_MUTATION,
  CAMAROO_SUBS_GRAPH_MUTATION,
  COURSE_PURCHASE_GRAPH_MUTATION,
} from "graphql/mutation/admin";
import TopBar from "./Filterbar";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export const options = {
  responsive: true,
  plugins: {
    legend: {
      display: false,
    },
  },
};

const GRAPH_COLORS = {
  users:          { border: "rgb(59, 130, 246)",  bg: "rgba(59, 130, 246, 0.2)" },
  portfolios:     { border: "rgb(139, 92, 246)",  bg: "rgba(139, 92, 246, 0.2)" },
  opportunities:  { border: "rgb(6, 182, 212)",   bg: "rgba(6, 182, 212, 0.2)" },
  subscriptions:  { border: "rgb(245, 158, 11)",  bg: "rgba(245, 158, 11, 0.2)" },
  coursePurchases:{ border: "rgb(16, 185, 129)",  bg: "rgba(16, 185, 129, 0.2)" },
  // legacy
  monthlyRevenue: { border: "rgb(239, 68, 68)",   bg: "rgba(239, 68, 68, 0.2)" },
  files:          { border: "rgb(75, 192, 192)",  bg: "rgba(75, 192, 192, 0.5)" },
  projects:       { border: "rgb(153, 102, 255)", bg: "rgba(153, 102, 255, 0.5)" },
  storage:        { border: "rgb(54, 162, 235)",  bg: "rgba(54, 162, 235, 0.5)" },
};

const graphConfig = {
  // ── Camaroo ──────────────────────────────────────────────────────────────────
  users: {
    mutation: CAMAROO_USER_GRAPH_MUTATION,
    responseKey: "userGraph",
    label: "Users",
    title: "Users",
  },
  portfolios: {
    mutation: PORTFOLIO_GRAPH_MUTATION,
    responseKey: "portfolioGraph",
    label: "Portfolios",
    title: "Portfolios",
  },
  opportunities: {
    mutation: OPPORTUNITY_GRAPH_MUTATION,
    responseKey: "opportunityGraph",
    label: "Opportunities",
    title: "Opportunities",
  },
  subscriptions: {
    mutation: CAMAROO_SUBS_GRAPH_MUTATION,
    responseKey: "subsGraph",
    label: "Subscriptions",
    title: "Subscriptions",
  },
  coursePurchases: {
    mutation: COURSE_PURCHASE_GRAPH_MUTATION,
    responseKey: "coursePurchaseGraph",
    label: "Course Purchases",
    title: "Course Purchases",
  },
  // ── Legacy (kept for backward compat) ────────────────────────────────────────
  monthlyRevenue: {
    mutation: GRAPH_MUTATION,
    responseKey: "graph",
    label: "Revenue (USD)",
    interval: "monthly",
    title: "Revenue",
  },
  files: {
    mutation: TRACK_GRAPH_MUTATION,
    responseKey: "trackGraph",
    label: "Files",
    title: "Files",
  },
  projects: {
    mutation: PROJECT_GRAPH_MUTATION,
    responseKey: "projectGraph",
    label: "Projects",
    title: "Projects",
  },
  storage: {
    mutation: STORAGE_GRAPH_MUTATION,
    responseKey: "userGraph",
    label: "Storage (GB)",
    title: "Storage",
  },
};

const DEFAULT_TYPE = "users";

function Graph({ type }) {
  const graphType = type || DEFAULT_TYPE;
  const config = useMemo(
    () => graphConfig[graphType] || graphConfig[DEFAULT_TYPE],
    [graphType]
  );

  const [fetchGraphData, { error }] = useMutation(config.mutation);
  const [interval, setInterval] = useState("monthly");
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState({ labels: [], values: [] });

  useEffect(() => {
    const updateGraph = async () => {
      setLoading(true);
      try {
        const { data } = await fetchGraphData({
          variables: { input: { interval } },
        });

        const graphResponse = data?.[config.responseKey];
        const monthlyData = graphResponse?.graphdata?.[0]?.arrMonth;

        const labels = [];
        const values = [];

        Object.values(monthlyData || {}).forEach((monthData) => {
          if (monthData && monthData.datetime) {
            let label = "";
            if (interval === "daily") {
              label = monthData.datetime;
            } else if (interval === "yearly") {
              label = moment(monthData.datetime, "YYYY-DD-MMM").format("MMM YYYY");
            } else {
              label = moment(monthData.datetime, "YYYY-DD-MMM").format("DD-MM-YYYY");
            }
            labels.push(label);
            values.push(monthData.value || 0);
          }
        });

        setChartData({ labels, values });
      } catch (err) {
        console.error("Error fetching graph data:", err);
      } finally {
        setLoading(false);
      }
    };

    updateGraph();
  }, [interval, graphType, config.responseKey]);

  const colors = GRAPH_COLORS[graphType] || GRAPH_COLORS.users;

  const data = {
    labels: chartData.labels,
    datasets: [
      {
        label: config.label,
        data: chartData.values,
        borderColor: colors.border,
        backgroundColor: colors.bg,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  if (error) return null;

  return (
    <>
      <TopBar
        interval={interval}
        setInterval={setInterval}
        loading={loading}
        title={config.title}
      />
      <div style={{ height: "auto", padding: "20px 15px 0", fontSize: "1.5rem" }}>
        <Line options={options} data={data} />
      </div>
    </>
  );
}

export default Graph;
